import HasCollectionPoints from '../../enums/HasCollectionPoints';
import PudoApi from '../api/pudo-api';
import {TooltipAndOverlayIDs} from '../components/detailed-tooltip/TooltipLibrary';
import {COLLECTION_INITIAL_NUMBER_POINTS} from '../reducers/PudoInitialState';
import {getPudo, getPudoCollectionPointAddress, getPudoSavedAddresses} from '../selectors/PudoSelectors';
import {getSettings, isGuest} from '../selectors/selectors';
import doMetric from '../tracking/bigQueryMetricRecorder';
import lookupAddressFieldDefinitionByCountryAndFieldName
from '../utils/lookupAddressFieldDefinitionByCountryAndFieldName';
import googleMapUtils from '../utils/googleMapUtils';
import Track from '../utils/tracking';
import * as types from './action-types';
import * as deliverySectionActions from './delivery-section-actions';
import {setGoogleMapsInitStatus, setGoogleMapsLoading} from './delivery-section-actions';
import {hideOverlay, showOverlay} from './overlay-actions';
import removeBlockedPudoSavedAddresses from './removeBlockedPudoSavedAddresses';

const mapZoomDisplay = 17;
const mapZoomDefault = 13;
const mapZoomFilter = 12;
const initialCollectionPointsToDisplay = COLLECTION_INITIAL_NUMBER_POINTS;

export function addPudoAddress(addressToAdd) {
  return {type: types.ADD_PUDO_ADDRESS_SUCCESS, addressToAdd};
}

export function changePudoAddress(addressToAdd, indexOfNewCollectionPoint) {
  return {type: types.CHANGE_PUDO_ADDRESS_SUCCESS, addressToAdd, indexOfNewCollectionPoint};
}

export function loadPudoSavedAddressesSuccess(savedAddresses, setInitialAddressLength) {
  return {type: types.LOAD_PUDO_SAVED_ADDRESSES_SUCCESS, savedAddresses, setInitialAddressLength};
}

export function getPudoCurrentLocationSuccess(currentLocation) {
  return {type: types.UPDATE_PUDO_CURRENT_POSITION_SUCCESS, currentLocation};
}

export function updatePudoCurrentLocationSuccess(currentLocation) {
  return {type: types.UPDATE_PUDO_CURRENT_POSITION_SUCCESS, currentLocation};
}

export function getPudoCollectionPointsSuccess(collectionPoints) {
  return {type: types.GET_PUDO_COLLECTION_POINTS_SUCCESS, collectionPoints};
}

export function updateSelectedCollectionPointSuccess(selectedCollectionPoint) {
  return {type: types.UPDATE_SELECTED_COLLECTION_POINT, selectedCollectionPoint};
}

export function setDisplayCollectorNameStatus(isDisplayCollectorName, selectedPudoAddressIndex) {
  return {type: types.SET_DISPLAY_COLLECTOR_NAME_STATUS, isDisplayCollectorName, selectedPudoAddressIndex};
}

export function setDisplayFiltersStatus(isDisplayFilters) {
  return {type: types.SET_DISPLAY_FILTERS_STATUS, isDisplayFilters};
}

export function setDisplayListViewStatus(isDisplayListView) {
  return {type: types.SET_DISPLAY_LIST_VIEW_STATUS, isDisplayListView};
}

export function displayCollectorName(index) {
  return setDisplayCollectorNameStatus(true, index);
}

export function removeCollectorName() {
  return setDisplayCollectorNameStatus(false, null);
}

export function hidePudoCollectionPoints() {
  return {type: types.HIDE_ADD_COLLECTION_POINT_SUCCESS, hideCollectionPoint: true};
}

export function setCollectorName(name) {
  return {type: types.SET_COLLECTOR_NAME, name};
}

export function setInitialCollectionPoints(collectionPoints) {
  return {type: types.SET_INITIAL_COLLECTION_POINTS, collectionPoints};
}

export function updateCollectionPoints(collectionPoints) {
  return {type: types.UPDATE_COLLECTION_POINTS, collectionPoints};
}

export function defaultMapZoom(defaultZoom = mapZoomDefault) {
  return {type: types.UPDATE_MAP_ZOOM, mapZoom: defaultZoom};
}

export function activateAddCollectionPoint() {
  return (dispatch, getState) => {
    dispatch({type: types.ACTIVATE_ADD_COLLECTION_POINT_SUCCESS});
    if (getSettings(getState()).enable_collection || getSettings(getState()).should_use_new_collection_overlay) {
      dispatch(showOverlay(TooltipAndOverlayIDs.COLLECTION));
    } else {
      dispatch(showOverlay('pudo'));
    }
  };
}

export function activateOverlay() {
  return (dispatch) => {
    dispatch(showOverlay(TooltipAndOverlayIDs.COLLECTION));
  };
}

export function showPudoMap() {
  return (dispatch, getState) => {
    const deliveryState = getState().delivery;
    if (deliveryState.isGoogleMapLoading) return;
    
    if (deliveryState.isGoogleMapsAvailable) {
      dispatch(activateAddCollectionPoint());
    } else {
      dispatch(setGoogleMapsLoading(true));
      return googleMapUtils.loadGoogleMaps()
        .then(() => {
          dispatch(setGoogleMapsInitStatus(true));
          dispatch(activateAddCollectionPoint());
        })
        .finally(() => {
          dispatch(setGoogleMapsLoading(false));
        });
    }
  };
}

export function deactivateAddCollectionPoint() {
  return {type: types.DEACTIVATE_ADD_COLLECTION_POINT_SUCCESS, isAddCollectionPoint: false};
}

export function enterCollectionPoint(key) {
  return {type: types.ENTER_COLLECTION_POINT, key};
}

export function makeActiveCollectionPoint(key) {
  return {type: types.MAKE_ACTIVE_COLLECTION_POINT, key};
}

export function updateSelectedActiveCollectionPoint(key) {
  return {type: types.UPDATE_SELECTED_ACTIVE_COLLECTION_POINT, key};
}

export function updateCollectionPointsAmountToDisplay(collectionPointsAmountToDisplay) {
  return {type: types.UPDATE_COLLECTION_POINTS_AMOUNT_TO_DISPLAY, collectionPointsAmountToDisplay};
}

export function updateCollectionPointsToDisplay(collectionPointsToDisplay) {
  return {type: types.UPDATE_COLLECTION_POINTS_TO_DISPLAY, collectionPointsToDisplay};
}

export function showMoreCollectionPoints() {
  return (dispatch, getState) => {
    const amountToDisplay = getState().pudo.collectionPointsAmountToDisplay + 8;
    const toDisplay = getState().pudo.initialCollectionPoints.slice(0, amountToDisplay);
    
    dispatch(updateCollectionPointsToDisplay(toDisplay));
    dispatch(updateCollectionPointsAmountToDisplay(amountToDisplay));
  };
}

export function updateHasCollectionPoints(hasCollectionPoints) {
  return {type: types.UPDATE_HAS_COLLECTION_POINTS, hasCollectionPoints};
}

export function updateFilterValuesToDefault() {
  return {type: types.SET_FILTER_VALUES_TO_DEFAULT, filterValues: []};
}

export function getPudoLocationPoints(country, postcode, locality, latlng, isCollection = false) {
  const useCollectionPointsFromAPI = (collectionPoints, dispatch) => {
    dispatch(updateHasCollectionPoints(HasCollectionPoints.PENDING));
    dispatch(getPudoCollectionPointsSuccess(collectionPoints));
    dispatch(updateFilterValuesToDefault());
    dispatch(updateCollectionPointsAmountToDisplay(initialCollectionPointsToDisplay));
    dispatch(setInitialCollectionPoints(collectionPoints));
    dispatch(updateCollectionPointsToDisplay(collectionPoints.slice(0, initialCollectionPointsToDisplay)));
    if (collectionPoints.length > 0) {
      return dispatch(updateHasCollectionPoints(HasCollectionPoints.POINTS_RETURNED));
    }
  
    return dispatch(updateHasCollectionPoints(HasCollectionPoints.NONE_RETURNED));
  };
  
  return (dispatch, getState) => {
    if (isCollection || window.start.settings.should_use_new_collection_overlay) {
      return PudoApi.getStores(country, postcode, locality, latlng, dispatch)
        .then(collectionPoints => useCollectionPointsFromAPI(collectionPoints, dispatch))
        .catch((e) => {
          Track.track(`Error retrieving collection location points ${e}`);
        });
    }
    
    const shouldValidatePostcodeForLocationsCall = getSettings(getState())?.should_use_extra_click_and_collect_postcode_validation;
    
    if (shouldValidatePostcodeForLocationsCall) {
      const postcodeFieldDefinition = lookupAddressFieldDefinitionByCountryAndFieldName(country, 'postCode');
      const isPostcodeRequired = postcodeFieldDefinition?.required;
  
      if (isPostcodeRequired && !postcode) {
        try {
          doMetric(
            'Invalid locations request',
            [
              {
                name: 'country',
                value: country,
              },
            ],
            [
              {
                type: 'Invalid locations request',
                label: 'Invalid locations request',
              },
            ],
            'impression',
          );
        } catch (e) {
          Track.error(e);
        }
        return dispatch(updateHasCollectionPoints(HasCollectionPoints.INVALID_LOCATION_REQUEST));
      }
    }
    
    return PudoApi.getCollectionPoints(country, postcode, locality, latlng)
      .then(collectionPoints => useCollectionPointsFromAPI(collectionPoints, dispatch))
      .catch(() => {
        Track.track('Error retrieving click & collect locations points');
      });
  };
}

export function loadPudoSavedAddresses(setInitialAddressLength = false) {
  return (dispatch, getState) => PudoApi.getAllPudoSavedAddresses()
    .then((savedAddresses) => {
      const blockedWordsForPudoSavedAddressesForLocales = getSettings(getState())?.blocked_words_for_pudo_saved_addresses_for_locales;
      
      let filteredAddresses = blockedWordsForPudoSavedAddressesForLocales
        ? removeBlockedPudoSavedAddresses(savedAddresses, blockedWordsForPudoSavedAddressesForLocales)
        : savedAddresses;
  
      if (getSettings(getState())?.display_last_used_store_only && filteredAddresses?.length > 1) {
        filteredAddresses = [filteredAddresses[0]];
      }
      
      if (filteredAddresses.length > 0) {
        dispatch(deliverySectionActions.updateValidStatus(true));
      }
      dispatch(loadPudoSavedAddressesSuccess(filteredAddresses, setInitialAddressLength));
    })
    .catch(() => {
      Track.track('Error while loading click & collect saved addresses');
    });
}

export function setCollectionPointLoading(status) {
  return {type: types.SET_COLLECTION_POINT_LOADING, collectionPointLoading: status};
}

export function getPudoCurrentLocation(isCollection = false, callback) {
  return dispatch => PudoApi.getCurrentLocation()
    .then((currentLocation) => {
      dispatch(getPudoCurrentLocationSuccess(currentLocation));
      dispatch(getPudoLocationPoints(currentLocation.country, currentLocation.postcode,
        currentLocation.locality, currentLocation.latlng, isCollection));
      dispatch(defaultMapZoom());
      return currentLocation;
    })
    .then((currentLocation) => {
      if (isCollection) {
        callback({
          geometry: {
            location: {
              lat: () => currentLocation.latlng.lat,
              lng: () => currentLocation.latlng.lng,
            },
          },
        });
      }
    })
    .catch((e) => {
      Track.track(`Error retrieving click & collect current location ${e}`);
    });
}

// User manually enter shipping address
export function updatePudoCurrentLocation(latlng, isCollection = false) {
  return dispatch => PudoApi.getAddress(latlng).then((currentLocation) => {
    dispatch(getPudoCurrentLocationSuccess(currentLocation));
    return dispatch(getPudoLocationPoints(currentLocation.country, currentLocation.postcode,
      currentLocation.locality, currentLocation.latlng, isCollection));
  });
}

// Collection point info
export function displayCollectionPoint(key, center) {
  const point = {
    isDisplayCollectionPointInfo: true,
    isDisplayPostcodeInput: false,
    displayCollectionPoint: key,
    mapZoom: mapZoomDisplay,
    mapCenter: center,
  };
  
  return {type: types.DISPLAY_COLLECTION_POINT_SUCCESS, point};
}

export function deactivateDisplayCollectionPointInfo() {
  const newState = {
    isDisplayCollectionPointInfo: false,
    isDisplayPostcodeInput: true,
    displayCollectionPoint: -1,
    mapZoom: mapZoomDefault,
  };
  
  return {type: types.DEACTIVATE_DISPLAY_COLLECTION_POINT_INFO_SUCCESS, newState};
}

export function displayCollectionPointInfo(index) {
  return (dispatch, getState) => {
    const collectionPoint = getState().pudo.collectionPoints[index];
    const center = {lat: collectionPoint.latitude, lng: collectionPoint.longitude};
    
    dispatch(displayCollectionPoint(index, center));
  };
}

export function addCollectionPoint(index) {
  return (dispatch) => {
    dispatch(displayCollectionPointInfo(index));
    dispatch(displayCollectorName(index));
  };
}

export function removeAddCollectionPoint() {
  return (dispatch) => {
    dispatch(deactivateDisplayCollectionPointInfo());
    dispatch(setCollectorName(''));
    dispatch(removeCollectorName());
  };
}

export function resetPudoMap(newOverlay) {
  return (dispatch) => {
    dispatch(deactivateDisplayCollectionPointInfo());
    if (!newOverlay) {
      dispatch(setCollectorName(''));
    }
    dispatch(removeCollectorName());
    dispatch(deactivateAddCollectionPoint());
    dispatch(deliverySectionActions.updateValidStatus(true));
  };
}

// Toggles/tabs
export function showFilters(displayFiltersStatus) {
  return (dispatch) => {
    dispatch(setDisplayFiltersStatus(displayFiltersStatus));
  };
}

export function showListView(displayListViewStatus) {
  return (dispatch) => {
    dispatch(setDisplayListViewStatus(displayListViewStatus));
  };
}

export function updateFilterState(newState) {
  return {type: types.SET_FILTER_COLLECTION_POINTS_SUCCESS, newState};
}

export function filterCollectionPoints(filterValues, collectionPoints, isMobile) {
  return (dispatch, getState) => {
    const firstCollectionPointCenter = {lat: collectionPoints[0]?.latitude, lng: collectionPoints[0]?.longitude};
    const toDisplay = collectionPoints.slice(0, getState().pudo.collectionPointsAmountToDisplay);
    
    const newState = {
      filterValues,
      collectionPoints,
      isDisplayFilters: false,
      mapZoom: isMobile ? mapZoomFilter : mapZoomDefault,
      mapCenter: firstCollectionPointCenter,
      selectedActiveCollectionPoint: 0,
    };
    
    dispatch(updateFilterState(newState));
    dispatch(updateCollectionPointsToDisplay(toDisplay));
  };
}

// Save a new collection point
export function saveNewCollectionPoint() {
  return (dispatch, getState) => {
    dispatch(hideOverlay());
    const location = getPudoCollectionPointAddress(getState());
    
    const point = {
      ...location.address,
      type: location.type,
      serviceId: (location?.deliveryOptions && location?.deliveryOptions[0]?.serviceId) || '',
      name: getState().pudo.collectorName,
      newCollectionPoint: true,
    };
    delete point.addressId;
    
    const isGuestCustomer = isGuest(getState());
    const saveOnPlaceOrderSettingEnabled = getSettings(getState()).save_collection_point_on_place_order;
    
    if (isGuestCustomer) {
      dispatch(resetPudoMap());
      dispatch(loadPudoSavedAddressesSuccess([point]));
    } else if (saveOnPlaceOrderSettingEnabled) {
      dispatch(resetPudoMap());
      
      const savedAddresses = getPudoSavedAddresses(getState()) || [];
      const indexOfNewCollectionPoint = savedAddresses.findIndex(address => address?.newCollectionPoint);
      
      if (indexOfNewCollectionPoint !== -1 && getSettings(getState()).change_new_pudo_collection_point) {
        dispatch(changePudoAddress(point, indexOfNewCollectionPoint));
      } else {
        dispatch(addPudoAddress(point));
      }
    } else {
      dispatch(setCollectionPointLoading(true));
      
      return PudoApi.saveNewCollectionPoint(point)
        .then(() => {
          dispatch(resetPudoMap());
          dispatch(loadPudoSavedAddresses());
        })
        .finally(() => {
          setTimeout(() => {
            dispatch(setCollectionPointLoading(false));
          }, 3500);
        });
    }
  };
}

export function saveNewCollectionPointUpdated() {
  return (dispatch, getState) => {
    dispatch(hideOverlay());
    const location = getPudoCollectionPointAddress(getState()) || getPudo(getState())?.collectionPoints[0];
    
    const point = {
      ...location.address,
      type: location.type,
      serviceId: (location?.deliveryOptions && location?.deliveryOptions[0]?.serviceId) || '',
      name: getState().pudo.collectorName,
      newCollectionPoint: true,
    };
    delete point.addressId;
    
    const isGuestCustomer = isGuest(getState());
    const saveOnPlaceOrderSettingEnabled = getSettings(getState()).save_collection_point_on_place_order;
    const newOverlay = getSettings(getState())?.should_use_new_collection_overlay;
    
    if (isGuestCustomer) {
      dispatch(resetPudoMap(newOverlay));
      dispatch(loadPudoSavedAddressesSuccess([point]));
    } else if (saveOnPlaceOrderSettingEnabled) {
      dispatch(resetPudoMap());
      
      const savedAddresses = getPudoSavedAddresses(getState()) || [];
      const indexOfNewCollectionPoint = savedAddresses.findIndex(address => address?.newCollectionPoint);
      
      if (indexOfNewCollectionPoint !== -1 && getSettings(getState()).change_new_pudo_collection_point) {
        dispatch(changePudoAddress(point, indexOfNewCollectionPoint));
      } else {
        dispatch(addPudoAddress(point));
      }
    } else {
      dispatch(setCollectionPointLoading(true));
      
      return PudoApi.saveNewCollectionPoint(point)
        .then(() => {
          dispatch(resetPudoMap());
          dispatch(loadPudoSavedAddresses());
        })
        .finally(() => {
          setTimeout(() => {
            dispatch(setCollectionPointLoading(false));
          }, 3500);
        });
    }
  };
}

export function saveNewCollectionPointUpdatedMobile() {
  return (dispatch, getState) => {
    dispatch(hideOverlay());
    const pudoCollectionPoint = getPudo(getState())?.selectedActiveCollectionPoint;
    const location = getPudo(getState())?.collectionPoints[pudoCollectionPoint];
    
    const point = {
      ...location.address,
      type: location.type,
      serviceId: (location?.deliveryOptions && location?.deliveryOptions[0]?.serviceId) || '',
      name: getState().pudo.collectorName,
      newCollectionPoint: true,
    };
    delete point.addressId;
    
    const isGuestCustomer = isGuest(getState());
    const saveOnPlaceOrderSettingEnabled = getSettings(getState()).save_collection_point_on_place_order;
    
    if (isGuestCustomer) {
      dispatch(resetPudoMap());
      dispatch(loadPudoSavedAddressesSuccess([point]));
    } else if (saveOnPlaceOrderSettingEnabled) {
      dispatch(resetPudoMap());
      
      const savedAddresses = getPudoSavedAddresses(getState()) || [];
      const indexOfNewCollectionPoint = savedAddresses.findIndex(address => address?.newCollectionPoint);
      
      if (indexOfNewCollectionPoint !== -1 && getSettings(getState()).change_new_pudo_collection_point) {
        dispatch(changePudoAddress(point, indexOfNewCollectionPoint));
      } else {
        dispatch(addPudoAddress(point));
      }
    } else {
      dispatch(setCollectionPointLoading(true));
      
      return PudoApi.saveNewCollectionPoint(point)
        .then(() => {
          dispatch(resetPudoMap());
          dispatch(loadPudoSavedAddresses());
        })
        .finally(() => {
          setTimeout(() => {
            dispatch(setCollectionPointLoading(false));
          }, 3500);
        });
    }
  };
}

export function updateSelectedCollectionPoint(selectedCollectionPoint) {
  return updateSelectedCollectionPointSuccess(selectedCollectionPoint);
}
